import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
    Breadcrumb, Card, Descriptions, Spin, Tag, Table, Space, Button,
    Modal, message, Form, Input, InputNumber, Select, Upload, Grid, Row, Col,
    Statistic, Avatar, Dropdown, Tooltip
} from 'antd';
import {
    RightOutlined, EditOutlined, ReloadOutlined, PlusOutlined,
    DeleteOutlined, UploadOutlined, SearchOutlined, HomeOutlined,
    EnvironmentOutlined, CheckCircleOutlined, ClockCircleOutlined,
    ShoppingCartOutlined, ArrowLeftOutlined, DownloadOutlined,
    FileExcelOutlined, FilePdfOutlined, FileTextOutlined,
    ExclamationCircleOutlined, SaveOutlined, AppstoreOutlined
} from '@ant-design/icons';
import { acheteTerrainService } from '../../common/services';

dayjs.extend(customParseFormat);

const { Option } = Select;

const statusConfig = {
    disponible: { color: 'green', text: 'Disponible', icon: <CheckCircleOutlined /> },
    reserve: { color: 'gold', text: 'Réservé', icon: <ClockCircleOutlined /> },
    réservé: { color: 'gold', text: 'Réservé', icon: <ClockCircleOutlined /> },
    reserver: { color: 'gold', text: 'Réservé', icon: <ClockCircleOutlined /> },
    vendu: { color: 'red', text: 'Vendu', icon: <ShoppingCartOutlined /> }
};

const getStatusConfig = (status) => {
    return statusConfig[status] || { color: 'default', text: status || 'Disponible', icon: <CheckCircleOutlined /> };
};

const currency = (n) =>
    typeof n === 'number'
        ? `${n.toLocaleString('fr-FR')} FCFA`
        : n
            ? `${Number(n).toLocaleString('fr-FR')} FCFA`
            : '—';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const formats = ['YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DD', 'DD/MM/YYYY'];
    for (const fmt of formats) {
        const d = dayjs(dateStr, fmt, true);
        if (d.isValid()) return d.format('DD/MM/YYYY HH:mm');
    }
    const d = dayjs(dateStr);
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
};

const getAvatarColor = (name) => {
    const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#eb2f96', '#52c41a'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const DetailSite = () => {
    const { id } = useParams();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lots, setLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(false);
    const [qLots, setQLots] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [editSiteOpen, setEditSiteOpen] = useState(false);
    const [lotModalOpen, setLotModalOpen] = useState(false);
    const [editingLot, setEditingLot] = useState(null);

    const [formSite] = Form.useForm();
    const [formLot] = Form.useForm();

    const filteredLots = useMemo(() => {
        let result = lots || [];
        if (statusFilter && statusFilter !== 'all') {
            result = result.filter((l) => {
                const s = (l.status || 'disponible').toLowerCase();
                if (statusFilter === 'reserve') {
                    return s === 'reserve' || s === 'réservé' || s === 'reserver';
                }
                return s === statusFilter;
            });
        }
        const q = (qLots || '').trim().toLowerCase();
        if (q) {
            result = result.filter((l) =>
                [l.intitule, l?.remarque, String(l?.superficieM2), String(l?.prixTotal)]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q))
            );
        }
        return result;
    }, [qLots, lots, statusFilter]);

    const stats = useCallback(() => {
        const total = lots.length;
        const disponibles = lots.filter(l => (l.status || 'disponible') === 'disponible').length;
        const reserves = lots.filter(l => ['reserve', 'réservé', 'reserver'].includes((l.status || '').toLowerCase())).length;
        const vendus = lots.filter(l => (l.status || '').toLowerCase() === 'vendu').length;
        const prixMoyen = total > 0 ? Math.round(lots.reduce((sum, l) => sum + (Number(l.prixTotal) || 0), 0) / total) : 0;
        const superficieTotale = lots.reduce((sum, l) => sum + (Number(l.superficieM2) || 0), 0);
        return { total, disponibles, reserves, vendus, prixMoyen, superficieTotale };
    }, [lots])();

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const s = await acheteTerrainService.getSiteById(id);
            setSite(s);
            setLots(s?.lots || []);
        } catch (e) {
            console.error(e);
            message.error("Impossible de charger le site.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const openEditSite = () => {
        formSite.setFieldsValue({
            nom: site?.nom,
            localisation: site?.localisation,
            estViabilise: site?.estViabilise ? 'true' : 'false',
        });
        setEditSiteOpen(true);
    };

    const submitEditSite = async () => {
        try {
            const values = await formSite.validateFields();
            const payload = {
                nom: values.nom?.trim(),
                localisation: values.localisation?.trim(),
                estViabilise: values.estViabilise === 'true',
            };
            const updated = await acheteTerrainService.updateSite(site._id, payload);
            setSite(updated?.site || updated);
            message.success("Site mis à jour avec succès");
            setEditSiteOpen(false);
        } catch (e) {
            if (e?.errorFields) return;
            console.error(e);
            message.error("Échec de la mise à jour.");
        }
    };

    const recompute = async () => {
        try {
            await acheteTerrainService.recomputeNombreLots(site._id);
            message.success("Nombre total de lots recalculé");
            load();
        } catch (e) {
            console.error(e);
            message.error("Échec du recalcul.");
        }
    };

    const openNewLot = () => {
        setEditingLot(null);
        formLot.resetFields();
        setLotModalOpen(true);
    };

    const openEditLot = (lot) => {
        setEditingLot(lot);
        formLot.setFieldsValue({
            intitule: lot.intitule,
            superficieM2: lot.superficieM2,
            prixTotal: lot.prixTotal,
            remarque: lot.remarque || '',
            status: lot.status || 'disponible',
        });
        setLotModalOpen(true);
    };

    const submitLot = async () => {
        try {
            const values = await formLot.validateFields();
            const payload = {
                intitule: values.intitule?.trim(),
                superficieM2: Number(values.superficieM2),
                prixTotal: Number(values.prixTotal),
                remarque: values.remarque?.trim() || undefined,
                status: values.status || undefined,
            };
            if (!editingLot) {
                const res = await acheteTerrainService.addLot(site._id, payload);
                setSite(res?.site || res);
                setLots(res?.site?.lots || res?.lots || []);
                message.success("Lot ajouté avec succès");
            } else {
                const res = await acheteTerrainService.updateLot(site._id, editingLot._id, payload);
                const updatedSite = res?.site || site;
                setSite(updatedSite);
                setLots(updatedSite?.lots || []);
                message.success("Lot mis à jour avec succès");
            }
            setLotModalOpen(false);
            setEditingLot(null);
        } catch (e) {
            if (e?.errorFields) return;
            console.error(e);
            message.error("Échec de l'enregistrement du lot.");
        }
    };

    const deleteLot = (lot) => {
        Modal.confirm({
            title: 'Confirmer la suppression',
            icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div style={{ marginTop: 12 }}>
                    <p style={{ marginBottom: 8 }}>Voulez-vous vraiment supprimer ce lot ?</p>
                    <Card size="small" style={{ background: '#fafafa' }}>
                        <p style={{ margin: 0 }}><strong>{lot.intitule}</strong></p>
                        <p style={{ margin: '4px 0 0 0', color: '#666' }}>{currency(lot.prixTotal)}</p>
                    </Card>
                </div>
            ),
            okText: 'Supprimer',
            cancelText: 'Annuler',
            okButtonProps: { danger: true },
            centered: true,
            onOk: async () => {
                try {
                    await acheteTerrainService.deleteLot(site._id, lot._id);
                    message.success("Lot supprimé avec succès");
                    load();
                } catch (e) {
                    const needForce = e?.status === 409 || String(e?.message || '').includes('vendu');
                    if (needForce) {
                        Modal.confirm({
                            title: "Suppression forcée requise",
                            icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                            content: (
                                <div>
                                    <p>Ce lot est marqué comme <Tag color="red">Vendu</Tag></p>
                                    <p>Confirmez-vous la suppression forcée ?</p>
                                </div>
                            ),
                            okText: "Forcer la suppression",
                            okButtonProps: { danger: true },
                            cancelText: "Annuler",
                            centered: true,
                            onOk: async () => {
                                try {
                                    await acheteTerrainService.deleteLot(site._id, lot._id, { force: true });
                                    message.success("Lot supprimé (forcé)");
                                    load();
                                } catch (err2) {
                                    console.error(err2);
                                    message.error("Échec de la suppression forcée.");
                                }
                            }
                        });
                    } else {
                        console.error(e);
                        message.error("Échec de la suppression.");
                    }
                }
            }
        });
    };

    const importJson = async (file) => {
        try {
            const text = await file.text();
            const json = JSON.parse(text);
            if (!Array.isArray(json)) throw new Error('Le fichier doit contenir un tableau JSON.');
            const res = await acheteTerrainService.importLotsJson(site._id, json);
            message.success(`Import effectué (${res?.inserted ?? json.length} lots)`);
            load();
        } catch (e) {
            console.error(e);
            message.error("Échec de l'import JSON.");
        }
        return false;
    };

    const exportExcel = () => {
        message.loading('Export Excel en cours...', 1);
        const data = filteredLots.map(lot => ({
            'Intitulé': lot.intitule || '',
            'Superficie (m²)': lot.superficieM2 || '',
            'Prix Total (FCFA)': lot.prixTotal || '',
            'Statut': getStatusConfig(lot.status).text,
            'Remarque': lot.remarque || ''
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Lots');
        XLSX.writeFile(wb, `lots_${site?.nom?.replace(/\s+/g, '_') || 'site'}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
        message.success('Export Excel terminé');
    };

    const exportCSV = () => {
        message.loading('Export CSV en cours...', 1);
        const data = filteredLots.map(lot => ({
            'Intitulé': lot.intitule || '',
            'Superficie (m²)': lot.superficieM2 || '',
            'Prix Total (FCFA)': lot.prixTotal || '',
            'Statut': getStatusConfig(lot.status).text,
            'Remarque': lot.remarque || ''
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `lots_${site?.nom?.replace(/\s+/g, '_') || 'site'}_${dayjs().format('YYYY-MM-DD')}.csv`;
        link.click();
        message.success('Export CSV terminé');
    };

    const exportPDF = () => {
        message.loading('Export PDF en cours...', 1);
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(`Lots - ${site?.nom || 'Site'}`, 14, 15);
        doc.setFontSize(10);
        doc.text(`Localisation: ${site?.localisation || ''}`, 14, 22);
        doc.text(`Export: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 28);
        const tableData = filteredLots.map(lot => [
            lot.intitule || '',
            lot.superficieM2 ? `${Number(lot.superficieM2).toLocaleString('fr-FR')} m²` : '',
            lot.prixTotal ? `${Number(lot.prixTotal).toLocaleString('fr-FR')} FCFA` : '',
            getStatusConfig(lot.status).text
        ]);
        doc.autoTable({
            startY: 35,
            head: [['Intitulé', 'Superficie', 'Prix Total', 'Statut']],
            body: tableData,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [250, 140, 22] }
        });
        doc.save(`lots_${site?.nom?.replace(/\s+/g, '_') || 'site'}_${dayjs().format('YYYY-MM-DD')}.pdf`);
        message.success('Export PDF terminé');
    };

    const exportMenuItems = [
        { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Exporter en Excel', onClick: exportExcel },
        { key: 'csv', icon: <FileTextOutlined style={{ color: '#1890ff' }} />, label: 'Exporter en CSV', onClick: exportCSV },
        { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'Exporter en PDF', onClick: exportPDF }
    ];

    const columnsLots = [
        {
            title: 'Lot',
            dataIndex: 'intitule',
            key: 'intitule',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar size={36} style={{ backgroundColor: getAvatarColor(text), fontWeight: 600 }}>
                        {(text || 'L').charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight: 500, color: '#262626' }}>{text}</div>
                        {record.remarque && (
                            <Tooltip title={record.remarque}>
                                <div style={{ fontSize: 12, color: '#8c8c8c', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.remarque}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: 'Superficie',
            dataIndex: 'superficieM2',
            key: 'superficieM2',
            render: (v) => <span style={{ fontWeight: 500 }}>{v ? `${Number(v).toLocaleString('fr-FR')} m²` : '—'}</span>,
            width: 140
        },
        {
            title: 'Prix total',
            dataIndex: 'prixTotal',
            key: 'prixTotal',
            render: (v) => <span style={{ fontWeight: 600, color: '#fa8c16' }}>{currency(v)}</span>,
            width: 180
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (s) => {
                const config = getStatusConfig(s);
                return <Tag color={config.color} icon={config.icon} style={{ borderRadius: 12, padding: '2px 10px' }}>{config.text}</Tag>;
            },
            width: 140
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, r) => (
                <Space size="small">
                    <Tooltip title="Modifier">
                        <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEditLot(r)} style={{ color: '#1890ff' }} />
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => deleteLot(r)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    if (loading || !site) {
        return (
            <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin tip="Chargement du site..." size="large" />
            </div>
        );
    }

    return (
        <div className="content-wrapper">
            <style>
                {`
                    .detail-site-table .ant-table-row:nth-child(odd) { background-color: #fafafa; }
                    .detail-site-table .ant-table-row:hover > td { background-color: #fff7e6 !important; }
                    .detail-site-table .ant-table-thead > tr > th { background: #fafafa; font-weight: 600; color: #595959; }
                    .stat-card-detail { border-radius: 12px; transition: all 0.3s ease; }
                    .stat-card-detail:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
                    .descriptions-card .ant-descriptions-item-label { background: #fafafa; font-weight: 500; }
                `}
            </style>

            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item><Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link></Breadcrumb.Item>
                    <Breadcrumb.Item><Link to="/dashboard/sites" style={{ color: '#fa8c16' }}>Sites</Link></Breadcrumb.Item>
                    <Breadcrumb.Item><span style={{ color: '#595959' }}>{site.nom}</span></Breadcrumb.Item>
                </Breadcrumb>

                {/* Statistics Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="stat-card-detail" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Total Lots</span>}
                                value={stats.total}
                                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                                prefix={<AppstoreOutlined style={{ marginRight: 4 }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="stat-card-detail" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Disponibles</span>}
                                value={stats.disponibles}
                                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                                prefix={<CheckCircleOutlined style={{ marginRight: 4 }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="stat-card-detail" style={{ background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Réservés</span>}
                                value={stats.reserves}
                                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                                prefix={<ClockCircleOutlined style={{ marginRight: 4 }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="stat-card-detail" style={{ background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Vendus</span>}
                                value={stats.vendus}
                                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                                prefix={<ShoppingCartOutlined style={{ marginRight: 4 }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={16} lg={8}>
                        <Card className="stat-card-detail" style={{ background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Prix moyen</span>}
                                value={stats.prixMoyen}
                                valueStyle={{ color: 'white', fontSize: isMobile ? 18 : 22, fontWeight: 700 }}
                                suffix={<span style={{ fontSize: 14 }}>FCFA</span>}
                                formatter={(value) => value.toLocaleString('fr-FR')}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Site Details Card */}
                <Card
                    className="descriptions-card"
                    title={<span><HomeOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Informations du site</span>}
                    style={{ marginBottom: 24, borderRadius: 12 }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                    extra={
                        <Space>
                            <Tooltip title="Recalculer le nombre de lots">
                                <Button icon={<ReloadOutlined />} onClick={recompute} size={controlSize} style={{ borderRadius: 8 }}>Recalculer</Button>
                            </Tooltip>
                            <Upload beforeUpload={importJson} showUploadList={false} accept=".json">
                                <Tooltip title="Importer des lots depuis un fichier JSON">
                                    <Button icon={<UploadOutlined />} size={controlSize} style={{ borderRadius: 8 }}>Importer lots</Button>
                                </Tooltip>
                            </Upload>
                        </Space>
                    }
                >
                    <Descriptions bordered size={controlSize} column={isMobile ? 1 : 2}>
                        <Descriptions.Item label="Nom du site">{site.nom}</Descriptions.Item>
                        <Descriptions.Item label="Localisation"><Space><EnvironmentOutlined style={{ color: '#fa8c16' }} />{site.localisation}</Space></Descriptions.Item>
                        <Descriptions.Item label="Viabilisé">{site.estViabilise ? <Tag color="green" icon={<CheckCircleOutlined />}>Oui</Tag> : <Tag color="default">Non</Tag>}</Descriptions.Item>
                        <Descriptions.Item label="Nombre de lots"><span style={{ fontWeight: 600, color: '#fa8c16' }}>{site?.nombreTotalLots ?? (site?.lots?.length || 0)}</span></Descriptions.Item>
                        <Descriptions.Item label="Superficie totale"><span style={{ fontWeight: 500 }}>{stats.superficieTotale.toLocaleString('fr-FR')} m²</span></Descriptions.Item>
                        <Descriptions.Item label="Valeur totale estimée"><span style={{ fontWeight: 600, color: '#52c41a' }}>{lots.reduce((sum, l) => sum + (Number(l.prixTotal) || 0), 0).toLocaleString('fr-FR')} FCFA</span></Descriptions.Item>
                        <Descriptions.Item label="Créé le">{formatDate(site.createdAt)}</Descriptions.Item>
                        <Descriptions.Item label="Mis à jour le">{formatDate(site.updatedAt)}</Descriptions.Item>
                    </Descriptions>
                </Card>

                {/* Lots Section */}
                <Card
                    title={<span><AppstoreOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Liste des Lots <Tag color="orange" style={{ marginLeft: 8 }}>{filteredLots.length}</Tag></span>}
                    style={{ borderRadius: 12 }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                >
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Rechercher un lot..."
                                allowClear
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                size={controlSize}
                                onChange={(e) => setQLots(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder="Filtrer par statut"
                                allowClear
                                size={controlSize}
                                style={{ width: '100%', borderRadius: 8 }}
                                onChange={(value) => setStatusFilter(value || 'all')}
                                value={statusFilter !== 'all' ? statusFilter : undefined}
                            >
                                <Option value="disponible"><Tag color="green" style={{ marginRight: 4 }}>●</Tag> Disponible</Option>
                                <Option value="reserve"><Tag color="gold" style={{ marginRight: 4 }}>●</Tag> Réservé</Option>
                                <Option value="vendu"><Tag color="red" style={{ marginRight: 4 }}>●</Tag> Vendu</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={10} style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                                <Button icon={<DownloadOutlined />} size={controlSize} style={{ borderRadius: 8 }}>Exporter</Button>
                            </Dropdown>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openNewLot}
                                size={controlSize}
                                style={{ borderRadius: 8, background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)', border: 'none' }}
                            >
                                Nouveau lot
                            </Button>
                        </Col>
                    </Row>

                    <Table
                        className="detail-site-table"
                        size={controlSize}
                        rowKey="_id"
                        loading={lotsLoading}
                        columns={columnsLots}
                        dataSource={filteredLots}
                        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} lots` }}
                        scroll={{ x: 800 }}
                        locale={{ emptyText: 'Aucun lot trouvé' }}
                    />
                </Card>
            </div>

            {/* Modal Éditer site */}
            <Modal
                title={<span><EditOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Modifier le site</span>}
                open={editSiteOpen}
                onCancel={() => setEditSiteOpen(false)}
                onOk={submitEditSite}
                okText="Enregistrer"
                cancelText="Annuler"
                okButtonProps={{ icon: <SaveOutlined />, style: { background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)', border: 'none', borderRadius: 8 } }}
                cancelButtonProps={{ style: { borderRadius: 8 } }}
                centered
                destroyOnClose
            >
                <Form layout="vertical" form={formSite} style={{ marginTop: 16 }}>
                    <Form.Item label="Nom du site" name="nom" rules={[{ required: true, message: 'Le nom est requis' }]}>
                        <Input placeholder="Nom du site" prefix={<HomeOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label="Localisation" name="localisation" rules={[{ required: true, message: 'La localisation est requise' }]}>
                        <Input placeholder="Localisation" prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label="Viabilisé" name="estViabilise" rules={[{ required: true, message: 'Veuillez sélectionner' }]}>
                        <Select placeholder="Sélectionner" style={{ borderRadius: 8 }}>
                            <Option value="true"><Tag color="green" style={{ marginRight: 4 }}>●</Tag> Oui</Option>
                            <Option value="false"><Tag color="default" style={{ marginRight: 4 }}>●</Tag> Non</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Lot (create/update) */}
            <Modal
                title={<span>{editingLot ? <EditOutlined style={{ color: '#fa8c16', marginRight: 8 }} /> : <PlusOutlined style={{ color: '#52c41a', marginRight: 8 }} />}{editingLot ? 'Modifier le lot' : 'Nouveau lot'}</span>}
                open={lotModalOpen}
                onCancel={() => { setLotModalOpen(false); setEditingLot(null); }}
                onOk={submitLot}
                okText="Enregistrer"
                cancelText="Annuler"
                okButtonProps={{ icon: <SaveOutlined />, style: { background: editingLot ? 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)' : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)', border: 'none', borderRadius: 8 } }}
                cancelButtonProps={{ style: { borderRadius: 8 } }}
                centered
                destroyOnClose
                width={500}
            >
                <Form layout="vertical" form={formLot} style={{ marginTop: 16 }}>
                    <Form.Item label="Intitulé" name="intitule" rules={[{ required: true, message: "L'intitulé est requis" }]}>
                        <Input placeholder="Ex: Lot A2" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Superficie (m²)" name="superficieM2" rules={[{ required: true, message: 'Superficie requise' }]}>
                                <InputNumber min={1} style={{ width: '100%', borderRadius: 8 }} placeholder="500" formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={(value) => value.replace(/\s/g, '')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Prix total (FCFA)" name="prixTotal" rules={[{ required: true, message: 'Prix requis' }]}>
                                <InputNumber min={1} step={1000} style={{ width: '100%', borderRadius: 8 }} placeholder="5 000 000" formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={(value) => value.replace(/\s/g, '')} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Remarque" name="remarque">
                        <Input.TextArea rows={3} placeholder="Remarque optionnelle..." style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label="Statut" name="status" initialValue="disponible">
                        <Select style={{ borderRadius: 8 }}>
                            <Option value="disponible"><Tag color="green" style={{ marginRight: 4 }}>●</Tag> Disponible</Option>
                            <Option value="reserve"><Tag color="gold" style={{ marginRight: 4 }}>●</Tag> Réservé</Option>
                            <Option value="vendu"><Tag color="red" style={{ marginRight: 4 }}>●</Tag> Vendu</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DetailSite;
